import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Calendar, Clock, Building2, BarChart3, Users } from 'lucide-react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import dashboardService, {
  DueDatesData,
  StatusCountsData,
  InspectionTypeCountsData,
  TradeCountsData,
  AverageTurnAroundTimeItem,
  ExecutiveDashboardStats,
} from '../services/dashboardService';
import { projectService } from '../services/projectService';
import { Project } from '../types/project';
import api from '../services/api';
import '../styles/Buttons.css';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, LineElement, PointElement, Title, Tooltip, Legend);

interface InspectionType {
  _id: string;
  name: string;
}

const ServiceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [activeTab, setActiveTab] = useState<'executive' | 'service'>('executive');
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  // Chart data states
  const [dueDates, setDueDates] = useState<DueDatesData>({ thisWeek: 0, nextWeek: 0, overdue: 0 });
  const [averageTurnAroundTime, setAverageTurnAroundTime] = useState<AverageTurnAroundTimeItem[]>([]);
  const [statusCounts, setStatusCounts] = useState<StatusCountsData>({});
  const [inspectionTypeCounts, setInspectionTypeCounts] = useState<InspectionTypeCountsData>({});
  const [tradeCounts, setTradeCounts] = useState<TradeCountsData>({});

  // Executive dashboard states
  const [executiveStats, setExecutiveStats] = useState<ExecutiveDashboardStats | null>(null);
  const [loadingExecutive, setLoadingExecutive] = useState(false);

  // Loading states for each chart
  const [loadingDueDates, setLoadingDueDates] = useState(false);
  const [loadingTurnAround, setLoadingTurnAround] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingInspectionType, setLoadingInspectionType] = useState(false);
  const [loadingTrade, setLoadingTrade] = useState(false);

  // Filter states
  const [statusChartInspectionTypeFilter, setStatusChartInspectionTypeFilter] = useState<string | null>(null);
  const [inspectionTypeChartStatusFilter, setInspectionTypeChartStatusFilter] = useState<string | null>(null);
  const [tradeChartInspectionTypeFilter, setTradeChartInspectionTypeFilter] = useState<string | null>(null);

  // Inspection types for dropdown
  const [inspectionTypes, setInspectionTypes] = useState<InspectionType[]>([]);
  const [inspectionTypeNameToId, setInspectionTypeNameToId] = useState<{ [name: string]: string }>({});

  useEffect(() => {
    loadProjects();
  }, [workspaceId]);

  useEffect(() => {
    if (selectedProjectId) {
      if (activeTab === 'service') {
        loadInspectionTypes();
        loadAllDashboardData();
      } else if (activeTab === 'executive') {
        loadExecutiveDashboardData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, activeTab]);

  const loadProjects = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const response = await projectService.getAll(workspaceId);
      setProjects(response.projects || []);

      // Auto-select first project
      if (response.projects.length > 0 && !selectedProjectId) {
        setSelectedProjectId(response.projects[0]._id);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInspectionTypes = async () => {
    if (!selectedProjectId) return;

    try {
      const response = await api.get(`/service-settings/${selectedProjectId}/inspection-types`);
      const types = response.data.items || [];
      setInspectionTypes(types);

      // Create name-to-ID mapping
      const mapping: { [name: string]: string } = {};
      types.forEach((type: InspectionType) => {
        mapping[type.name] = type._id;
      });
      setInspectionTypeNameToId(mapping);
    } catch (error) {
      console.error('Error loading inspection types:', error);
    }
  };

  const loadAllDashboardData = async () => {
    if (!workspaceId || !selectedProjectId) return;

    await Promise.all([
      loadDueDates(),
      loadAverageTurnAroundTime(),
      loadStatusCounts(),
      loadInspectionTypeCounts(),
      loadTradeCounts(),
    ]);
  };

  const loadDueDates = async () => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      setLoadingDueDates(true);
      const data = await dashboardService.getDueDates(workspaceId, selectedProjectId);
      setDueDates(data);
    } catch (error) {
      console.error('Error loading due dates:', error);
    } finally {
      setLoadingDueDates(false);
    }
  };

  const loadAverageTurnAroundTime = async () => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      setLoadingTurnAround(true);
      const data = await dashboardService.getAverageTurnAroundTime(workspaceId, selectedProjectId);
      setAverageTurnAroundTime(data);
    } catch (error) {
      console.error('Error loading average turn around time:', error);
    } finally {
      setLoadingTurnAround(false);
    }
  };

  const loadStatusCounts = async () => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      setLoadingStatus(true);

      // Convert inspection type name to ID if filter is set
      const inspectionTypeId = statusChartInspectionTypeFilter
        ? inspectionTypeNameToId[statusChartInspectionTypeFilter]
        : undefined;

      const data = await dashboardService.getDeficienciesByStatus(
        workspaceId,
        selectedProjectId,
        inspectionTypeId
      );
      setStatusCounts(data);
    } catch (error) {
      console.error('Error loading status counts:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const loadInspectionTypeCounts = async () => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      setLoadingInspectionType(true);
      const data = await dashboardService.getDeficienciesByInspectionType(
        workspaceId,
        selectedProjectId,
        inspectionTypeChartStatusFilter || undefined
      );
      setInspectionTypeCounts(data);
    } catch (error) {
      console.error('Error loading inspection type counts:', error);
    } finally {
      setLoadingInspectionType(false);
    }
  };

  const loadTradeCounts = async () => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      setLoadingTrade(true);

      // Convert inspection type name to ID if filter is set
      const inspectionTypeId = tradeChartInspectionTypeFilter
        ? inspectionTypeNameToId[tradeChartInspectionTypeFilter]
        : undefined;

      const data = await dashboardService.getDeficienciesByTrade(
        workspaceId,
        selectedProjectId,
        inspectionTypeId
      );
      setTradeCounts(data);
    } catch (error) {
      console.error('Error loading trade counts:', error);
    } finally {
      setLoadingTrade(false);
    }
  };

  const loadExecutiveDashboardData = async () => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      setLoadingExecutive(true);
      // Load inspection types first to create name-to-ID mapping
      await loadInspectionTypes();
      const data = await dashboardService.getExecutiveDashboardStats(workspaceId, selectedProjectId);
      setExecutiveStats(data);
    } catch (error) {
      console.error('Error loading executive dashboard data:', error);
    } finally {
      setLoadingExecutive(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    // Reset filters when project changes
    setStatusChartInspectionTypeFilter(null);
    setInspectionTypeChartStatusFilter(null);
    setTradeChartInspectionTypeFilter(null);
  };

  // Helper function to build deficiency list URL with filters
  const buildDeficiencyUrl = (filters: {
    projectId?: string;
    inspectionType?: string;
    status?: string;
    trade?: string;
    sortBy?: string;
    sortOrder?: string;
    ageMin?: number;
    ageMax?: number;
  }) => {
    const params = new URLSearchParams();

    if (filters.projectId) params.set('projectId', filters.projectId);
    if (filters.inspectionType) params.set('inspectionType', filters.inspectionType);
    if (filters.status) params.set('status', filters.status);
    if (filters.trade) params.set('trade', filters.trade);
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);
    if (filters.ageMin !== undefined) params.set('ageMin', filters.ageMin.toString());
    if (filters.ageMax !== undefined) params.set('ageMax', filters.ageMax.toString());

    return `/workspaces/${workspaceId}/service/deficiencies?${params.toString()}`;
  };

  // Handle clicking on deficiency count
  const handleDeficiencyCountClick = (inspectionTypeName: string) => {
    // If it's "Overall", show all deficiencies without inspection type filter
    if (inspectionTypeName === 'Overall') {
      const url = buildDeficiencyUrl({
        projectId: selectedProjectId
      });
      window.open(url, '_blank');
      return;
    }

    const inspectionTypeId = inspectionTypeNameToId[inspectionTypeName];
    if (!inspectionTypeId) {
      console.warn(`No inspection type ID found for: ${inspectionTypeName}`);
      return;
    }

    const url = buildDeficiencyUrl({
      projectId: selectedProjectId,
      inspectionType: inspectionTypeId
    });
    window.open(url, '_blank');
  };

  // Handle clicking on oldest open item
  const handleOldestOpenClick = (inspectionTypeName: string) => {
    // Use the same status filter as the dashboard API: all "open" statuses
    const openStatuses = 'open,assigned,accepted,not-accepted,reopened';

    // If it's "Overall", show all open deficiencies sorted by oldest
    if (inspectionTypeName === 'Overall') {
      const url = buildDeficiencyUrl({
        projectId: selectedProjectId,
        status: openStatuses,
        sortBy: 'createdAt',
        sortOrder: 'asc'
      });
      window.open(url, '_blank');
      return;
    }

    const inspectionTypeId = inspectionTypeNameToId[inspectionTypeName];
    if (!inspectionTypeId) {
      console.warn(`No inspection type ID found for: ${inspectionTypeName}`);
      return;
    }

    const url = buildDeficiencyUrl({
      projectId: selectedProjectId,
      inspectionType: inspectionTypeId,
      status: openStatuses,
      sortBy: 'createdAt',
      sortOrder: 'asc'
    });
    window.open(url, '_blank');
  };

  // Handle clicking on age-based deficiencies
  const handleAgeRangeClick = (ageRange: string) => {
    // Define age ranges in days to match executive dashboard API logic
    // Dashboard uses: 0-7 (age<=7), 8-14 (7<age<=14), 15-30 (14<age<=30), 30+ (age>30)
    let ageMin: number | undefined;
    let ageMax: number | undefined;

    switch (ageRange) {
      case '0-7 days':
        ageMin = 0;
        ageMax = 7;
        break;
      case '8-14 days':
        ageMin = 7; // Start just after 7 days (exclusive)
        ageMax = 14;
        break;
      case '15-30 days':
        ageMin = 14; // Start just after 14 days (exclusive)
        ageMax = 30;
        break;
      case '30+ days':
        ageMin = 30; // Start just after 30 days (exclusive)
        ageMax = undefined; // No upper limit
        break;
    }

    const url = buildDeficiencyUrl({
      projectId: selectedProjectId,
      // Use the same status filter as the dashboard API: all "open" statuses
      status: 'open,assigned,accepted,not-accepted,reopened',
      sortBy: 'createdAt',
      sortOrder: ageRange === '0-7 days' ? 'desc' : 'asc',
      ageMin,
      ageMax
    });
    window.open(url, '_blank');
  };

  // Handle clicking on trade total deficiencies
  const handleTradeDeficiencyClick = (tradeId: string) => {
    const url = buildDeficiencyUrl({
      projectId: selectedProjectId,
      trade: tradeId
    });
    window.open(url, '_blank');
  };

  // Handle clicking on trade closed deficiencies
  const handleTradeClosedClick = (tradeId: string) => {
    const url = buildDeficiencyUrl({
      projectId: selectedProjectId,
      trade: tradeId,
      status: 'closed'
    });
    window.open(url, '_blank');
  };

  // Handle clicking on trade open deficiencies
  const handleTradeOpenClick = (tradeId: string) => {
    const openStatuses = 'open,assigned,accepted,not-accepted,reopened';
    const url = buildDeficiencyUrl({
      projectId: selectedProjectId,
      trade: tradeId,
      status: openStatuses
    });
    window.open(url, '_blank');
  };

  // Handle clicking on trade past due deficiencies
  const handleTradePastDueClick = (tradeId: string) => {
    const openStatuses = 'open,assigned,accepted,not-accepted,reopened';
    const url = buildDeficiencyUrl({
      projectId: selectedProjectId,
      trade: tradeId,
      status: openStatuses
    });
    // Note: Past due filtering would need additional URL parameter support
    // For now, this opens all open deficiencies for the trade
    window.open(url, '_blank');
  };

  // Handle clicking on trade oldest open item
  const handleTradeOldestOpenClick = (tradeId: string) => {
    const openStatuses = 'open,assigned,accepted,not-accepted,reopened';
    const url = buildDeficiencyUrl({
      projectId: selectedProjectId,
      trade: tradeId,
      status: openStatuses,
      sortBy: 'createdAt',
      sortOrder: 'asc'
    });
    window.open(url, '_blank');
  };

  // Chart options for status counts
  const statusChartData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        label: 'Deficiencies',
        data: Object.values(statusCounts),
        backgroundColor: '#48182F',
        borderRadius: 6,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      },
    ],
  };

  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(72, 24, 47, 0.95)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 'bold' as const,
        },
        bodyFont: {
          size: 12,
        },
        borderColor: 'rgba(72, 24, 47, 0.2)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#6B7280',
        },
        border: {
          color: 'rgba(212, 203, 196, 0.5)',
          width: 1.5,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(212, 203, 196, 0.2)',
          lineWidth: 0.5,
        },
        ticks: {
          precision: 0,
          font: {
            size: 11,
          },
          color: '#6B7280',
        },
        border: {
          color: 'rgba(212, 203, 196, 0.5)',
          width: 1.5,
        },
      },
    },
  };

  // Chart options for inspection type counts
  const inspectionTypeChartData = {
    labels: Object.keys(inspectionTypeCounts),
    datasets: [
      {
        label: 'Deficiencies',
        data: Object.values(inspectionTypeCounts),
        backgroundColor: '#6366F1',
        borderRadius: 6,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      },
    ],
  };

  // Chart options for trade counts
  const tradeChartData = {
    labels: Object.keys(tradeCounts),
    datasets: [
      {
        label: 'Deficiencies',
        data: Object.values(tradeCounts),
        backgroundColor: '#10B981',
        borderRadius: 6,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      },
    ],
  };

  // Chart options for average turn around time
  const turnAroundTimeChartData = {
    labels: averageTurnAroundTime.map(item => item.inspectionTypeName),
    datasets: [
      {
        label: 'Average Days',
        data: averageTurnAroundTime.map(item => item.averageDays),
        backgroundColor: '#F59E0B',
        borderRadius: 6,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      },
    ],
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces}>
      <div className="page-container">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0 }}>Service Dashboard</h1>
            <p style={{ fontSize: '14px', color: '#6B7280', margin: '8px 0 0 0' }}>
              Overview of all service activities
            </p>
          </div>

          {/* Project Selector */}
          {projects.length > 0 && (
            <div style={{ minWidth: '250px' }}>
              <select
                value={selectedProjectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#48182F',
                  backgroundColor: 'white',
                  border: '1.5px solid #D4CBC4',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  outline: 'none',
                  boxShadow: '0 1px 3px rgba(72, 24, 47, 0.08)',
                  transition: 'all 0.2s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#48182F';
                  e.target.style.boxShadow = '0 0 0 3px rgba(72, 24, 47, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#D4CBC4';
                  e.target.style.boxShadow = '0 1px 3px rgba(72, 24, 47, 0.08)';
                }}
              >
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Tabs Navigation */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '24px',
          borderBottom: '2px solid #D4CBC4',
          paddingBottom: '0'
        }}>
          <button
            onClick={() => setActiveTab('executive')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'executive' ? '#48182F' : '#6B7280',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'executive' ? '3px solid #48182F' : '3px solid transparent',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s',
              marginBottom: '-2px',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'executive') {
                e.currentTarget.style.color = '#48182F';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'executive') {
                e.currentTarget.style.color = '#6B7280';
              }
            }}
          >
            Executive Dashboard
          </button>
          <button
            onClick={() => setActiveTab('service')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'service' ? '#48182F' : '#6B7280',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'service' ? '3px solid #48182F' : '3px solid transparent',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s',
              marginBottom: '-2px',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'service') {
                e.currentTarget.style.color = '#48182F';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'service') {
                e.currentTarget.style.color = '#6B7280';
              }
            }}
          >
            Service Dashboard
          </button>
        </div>

        <div className="page-content" style={{ marginTop: '24px' }}>
          {activeTab === 'executive' ? (
            // Executive Dashboard Tab Content
            loadingExecutive ? (
              <div className="builders-loading">
                <div className="builders-loading-spinner" />
              </div>
            ) : !executiveStats ? (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '48px',
                textAlign: 'center',
                color: '#6B7280',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <BarChart3 size={48} style={{ color: '#D4CBC4', marginBottom: '16px' }} />
                <p style={{ fontSize: '16px', margin: 0 }}>No data available</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Project Deficiency Health */}
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
                }}>
                  <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                    Project Deficiency Health
                  </h2>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '13px'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F5F3F0' }}>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #D4CBC4', fontWeight: '600' }}>KPI</th>
                          {executiveStats.healthByInspectionType.map((item, idx) => (
                            <th key={idx} style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4', fontWeight: '600' }}>
                              {item.inspectionType}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', fontWeight: '500' }}>Total Deficiencies Logged</td>
                          {executiveStats.healthByInspectionType.map((item, idx) => (
                            <td key={idx} style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', textAlign: 'center' }}>
                              <span
                                onClick={() => handleDeficiencyCountClick(item.inspectionType)}
                                style={{
                                  color: '#48182F',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  textDecorationStyle: 'dotted',
                                  textUnderlineOffset: '3px'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = '#6B1F3F';
                                  e.currentTarget.style.textDecoration = 'underline solid';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = '#48182F';
                                  e.currentTarget.style.textDecoration = 'underline dotted';
                                }}
                                title="Click to view all deficiencies for this inspection type"
                              >
                                {item.totalDeficiencies}
                              </span>
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', fontWeight: '500' }}>Open Deficiencies</td>
                          {executiveStats.healthByInspectionType.map((item, idx) => (
                            <td key={idx} style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', textAlign: 'center' }}>
                              {item.openDeficiencies} ({item.openPercentage}%)
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', fontWeight: '500' }}>Avg. Deficiencies per Unit</td>
                          {executiveStats.healthByInspectionType.map((item, idx) => (
                            <td key={idx} style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', textAlign: 'center' }}>
                              {item.avgDeficienciesPerUnit}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', fontWeight: '500' }}>Avg. Close-Out Time</td>
                          {executiveStats.healthByInspectionType.map((item, idx) => (
                            <td key={idx} style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', textAlign: 'center' }}>
                              {item.avgCloseOutTime}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', fontWeight: '500' }}>% Closed Within Target (Due Date Assigned)</td>
                          {executiveStats.healthByInspectionType.map((item, idx) => (
                            <td key={idx} style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', textAlign: 'center' }}>
                              {item.closedWithinTarget}%
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', fontWeight: '500' }}>Past Due Items (deficiencies over 15 days old past pcc)</td>
                          {executiveStats.healthByInspectionType.map((item, idx) => (
                            <td key={idx} style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', textAlign: 'center' }}>
                              {item.pastDueCount}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', fontWeight: '500' }}>% of Open Deficiencies Past Due</td>
                          {executiveStats.healthByInspectionType.map((item, idx) => (
                            <td key={idx} style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', textAlign: 'center' }}>
                              {item.pastDuePercentage}%
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td style={{ padding: '12px', fontWeight: '500' }}>Oldest Open Item</td>
                          {executiveStats.healthByInspectionType.map((item, idx) => (
                            <td key={idx} style={{ padding: '12px', textAlign: 'center' }}>
                              <span
                                onClick={() => handleOldestOpenClick(item.inspectionType)}
                                style={{
                                  color: '#48182F',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  textDecorationStyle: 'dotted',
                                  textUnderlineOffset: '3px'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = '#6B1F3F';
                                  e.currentTarget.style.textDecoration = 'underline solid';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = '#48182F';
                                  e.currentTarget.style.textDecoration = 'underline dotted';
                                }}
                                title="Click to view oldest open deficiencies for this inspection type"
                              >
                                {item.oldestOpenDays}
                              </span>
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Open Deficiencies by Age */}
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
                }}>
                  <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                    Open Deficiencies by Age (Days)
                  </h2>
                  <div style={{ marginBottom: '24px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F5F3F0' }}>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4' }}>0-7 days</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4' }}>8-14 days</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4' }}>15-30 days</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4' }}>30+ days</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>
                            <span
                              onClick={() => handleAgeRangeClick('0-7 days')}
                              style={{
                                color: '#48182F',
                                fontWeight: '600',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationStyle: 'dotted',
                                textUnderlineOffset: '3px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#6B1F3F';
                                e.currentTarget.style.textDecoration = 'underline solid';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#48182F';
                                e.currentTarget.style.textDecoration = 'underline dotted';
                              }}
                              title="Click to view open deficiencies (0-7 days old)"
                            >
                              {executiveStats.openDeficienciesByAge['0-7 days']}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>
                            <span
                              onClick={() => handleAgeRangeClick('8-14 days')}
                              style={{
                                color: '#48182F',
                                fontWeight: '600',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationStyle: 'dotted',
                                textUnderlineOffset: '3px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#6B1F3F';
                                e.currentTarget.style.textDecoration = 'underline solid';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#48182F';
                                e.currentTarget.style.textDecoration = 'underline dotted';
                              }}
                              title="Click to view open deficiencies (8-14 days old)"
                            >
                              {executiveStats.openDeficienciesByAge['8-14 days']}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>
                            <span
                              onClick={() => handleAgeRangeClick('15-30 days')}
                              style={{
                                color: '#48182F',
                                fontWeight: '600',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationStyle: 'dotted',
                                textUnderlineOffset: '3px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#6B1F3F';
                                e.currentTarget.style.textDecoration = 'underline solid';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#48182F';
                                e.currentTarget.style.textDecoration = 'underline dotted';
                              }}
                              title="Click to view open deficiencies (15-30 days old)"
                            >
                              {executiveStats.openDeficienciesByAge['15-30 days']}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>
                            <span
                              onClick={() => handleAgeRangeClick('30+ days')}
                              style={{
                                color: '#48182F',
                                fontWeight: '600',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationStyle: 'dotted',
                                textUnderlineOffset: '3px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#6B1F3F';
                                e.currentTarget.style.textDecoration = 'underline solid';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#48182F';
                                e.currentTarget.style.textDecoration = 'underline dotted';
                              }}
                              title="Click to view open deficiencies (30+ days old)"
                            >
                              {executiveStats.openDeficienciesByAge['30+ days']}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div style={{ height: '80px' }}>
                    <Bar
                      data={{
                        labels: [''],
                        datasets: [
                          {
                            label: '0-7 days',
                            data: [executiveStats.openDeficienciesByAge['0-7 days']],
                            backgroundColor: '#60A5FA',
                          },
                          {
                            label: '8-14 days',
                            data: [executiveStats.openDeficienciesByAge['8-14 days']],
                            backgroundColor: '#EF4444',
                          },
                          {
                            label: '15-30 days',
                            data: [executiveStats.openDeficienciesByAge['15-30 days']],
                            backgroundColor: '#84CC16',
                          },
                          {
                            label: '30+ days',
                            data: [executiveStats.openDeficienciesByAge['30+ days']],
                            backgroundColor: '#A78BFA',
                          },
                        ],
                      }}
                      options={{
                        indexAxis: 'y',
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          x: {
                            stacked: true,
                            display: false,
                          },
                          y: {
                            stacked: true,
                            display: false,
                          },
                        },
                        plugins: {
                          legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                              usePointStyle: true,
                              padding: 15,
                              font: {
                                size: 12,
                              },
                            },
                          },
                          tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            padding: 12,
                            displayColors: true,
                            usePointStyle: true,
                            callbacks: {
                              label: (context) => {
                                return `${context.dataset.label}: ${context.parsed.x}`;
                              },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                {/* Quality Signals - Systemic Issues */}
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
                }}>
                  <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F' }}>
                    Quality Signals - Systemic Issues
                  </h2>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '13px'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F5F3F0' }}>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #D4CBC4' }}>Assignee</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4' }}>Total Deficiencies</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4' }}>% of Total</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4' }}>Avg. Close-Out Time</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4' }}>Closed</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4' }}>Open (Total)</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4' }}>Past Due</th>
                          <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #D4CBC4' }}>Oldest Open Item</th>
                        </tr>
                      </thead>
                      <tbody>
                        {executiveStats.tradeMetrics.map((trade, idx) => (
                          <tr key={idx} style={{ borderBottom: idx < executiveStats.tradeMetrics.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
                            <td style={{ padding: '12px', fontWeight: '500' }}>{trade.trade}</td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {trade.tradeId ? (
                                <span
                                  onClick={() => handleTradeDeficiencyClick(trade.tradeId!)}
                                  style={{
                                    color: '#48182F',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    textUnderlineOffset: '3px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#6B1F3F';
                                    e.currentTarget.style.textDecoration = 'underline solid';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#48182F';
                                    e.currentTarget.style.textDecoration = 'underline dotted';
                                  }}
                                  title={`Click to view all deficiencies for ${trade.trade}`}
                                >
                                  {trade.totalDeficiencies}
                                </span>
                              ) : (
                                <span>{trade.totalDeficiencies}</span>
                              )}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>{trade.percentageOfTotal}%</td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>{trade.avgCloseOutTime}</td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {trade.tradeId ? (
                                <span
                                  onClick={() => handleTradeClosedClick(trade.tradeId!)}
                                  style={{
                                    color: '#48182F',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    textUnderlineOffset: '3px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#6B1F3F';
                                    e.currentTarget.style.textDecoration = 'underline solid';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#48182F';
                                    e.currentTarget.style.textDecoration = 'underline dotted';
                                  }}
                                  title={`Click to view closed deficiencies for ${trade.trade}`}
                                >
                                  {trade.closedDeficiencies}
                                </span>
                              ) : (
                                <span>{trade.closedDeficiencies}</span>
                              )}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {trade.tradeId ? (
                                <span
                                  onClick={() => handleTradeOpenClick(trade.tradeId!)}
                                  style={{
                                    color: '#48182F',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    textUnderlineOffset: '3px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#6B1F3F';
                                    e.currentTarget.style.textDecoration = 'underline solid';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#48182F';
                                    e.currentTarget.style.textDecoration = 'underline dotted';
                                  }}
                                  title={`Click to view open deficiencies for ${trade.trade}`}
                                >
                                  {trade.openDeficiencies}
                                </span>
                              ) : (
                                <span>{trade.openDeficiencies}</span>
                              )}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {trade.tradeId ? (
                                <span
                                  onClick={() => handleTradePastDueClick(trade.tradeId!)}
                                  style={{
                                    color: '#48182F',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    textUnderlineOffset: '3px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#6B1F3F';
                                    e.currentTarget.style.textDecoration = 'underline solid';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#48182F';
                                    e.currentTarget.style.textDecoration = 'underline dotted';
                                  }}
                                  title={`Click to view past due deficiencies for ${trade.trade}`}
                                >
                                  {trade.pastDueDeficiencies}
                                </span>
                              ) : (
                                <span>{trade.pastDueDeficiencies}</span>
                              )}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {trade.tradeId ? (
                                <span
                                  onClick={() => handleTradeOldestOpenClick(trade.tradeId!)}
                                  style={{
                                    color: '#48182F',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    textUnderlineOffset: '3px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#6B1F3F';
                                    e.currentTarget.style.textDecoration = 'underline solid';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#48182F';
                                    e.currentTarget.style.textDecoration = 'underline dotted';
                                  }}
                                  title={`Click to view oldest open deficiencies for ${trade.trade}`}
                                >
                                  {trade.oldestOpenDays}
                                </span>
                              ) : (
                                <span>{trade.oldestOpenDays}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Deficiency Summary */}
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
                }}>
                  <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px', color: '#48182F', textAlign: 'center' }}>
                    Deficiency Summary
                  </h2>
                  <div style={{ height: `${executiveStats.tradeMetrics.length * 35 + 80}px` }}>
                    <Bar
                      data={{
                        labels: executiveStats.tradeMetrics.map(t => t.trade),
                        datasets: [
                          {
                            label: 'Closed',
                            data: executiveStats.tradeMetrics.map(t => t.closedDeficiencies),
                            backgroundColor: '#10B981',
                            borderRadius: 4,
                          },
                          {
                            label: 'Past Due',
                            data: executiveStats.tradeMetrics.map(t => t.pastDueDeficiencies),
                            backgroundColor: '#EF4444',
                            borderRadius: 4,
                          },
                          {
                            label: 'Open',
                            data: executiveStats.tradeMetrics.map(t => Math.max(0, t.openDeficiencies - t.pastDueDeficiencies)),
                            backgroundColor: '#FBBF24',
                            borderRadius: 4,
                          },
                          {
                            label: 'Other',
                            data: executiveStats.tradeMetrics.map(t => Math.max(0, t.totalDeficiencies - t.closedDeficiencies - t.openDeficiencies)),
                            backgroundColor: '#D1D5DB',
                            borderRadius: 4,
                          },
                        ].filter(dataset => dataset.data.some(val => val > 0)),
                      }}
                      options={{
                        indexAxis: 'y',
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                          mode: 'y',
                          intersect: false,
                        },
                        scales: {
                          x: {
                            stacked: true,
                            grid: {
                              display: true,
                              color: '#E5E7EB',
                            },
                            ticks: {
                              font: {
                                size: 11,
                              },
                              color: '#6B7280',
                            },
                          },
                          y: {
                            stacked: true,
                            grid: {
                              display: false,
                            },
                            ticks: {
                              font: {
                                size: 12,
                              },
                              color: '#374151',
                            },
                          },
                        },
                        plugins: {
                          legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                              usePointStyle: true,
                              padding: 15,
                              font: {
                                size: 12,
                              },
                            },
                          },
                          tooltip: {
                            mode: 'y',
                            intersect: false,
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            padding: 12,
                            displayColors: true,
                            usePointStyle: true,
                            titleFont: {
                              size: 13,
                              weight: 'bold',
                            },
                            bodyFont: {
                              size: 11,
                            },
                            callbacks: {
                              label: (context) => {
                                const dataIndex = context.dataIndex;
                                const trade = executiveStats.tradeMetrics[dataIndex];
                                const openNotPastDue = Math.max(0, trade.openDeficiencies - trade.pastDueDeficiencies);
                                const other = Math.max(0, trade.totalDeficiencies - trade.closedDeficiencies - trade.openDeficiencies);

                                const labels = [
                                  `Closed: ${trade.closedDeficiencies}`,
                                  `Past Due: ${trade.pastDueDeficiencies}`,
                                  `Open: ${openNotPastDue}`,
                                ];

                                if (other > 0) {
                                  labels.push(`Other: ${other}`);
                                }

                                return labels[context.datasetIndex] || '';
                              },
                              labelColor: (context) => {
                                const colors = ['#10B981', '#EF4444', '#FBBF24', '#D1D5DB'];
                                return {
                                  borderColor: colors[context.datasetIndex],
                                  backgroundColor: colors[context.datasetIndex],
                                  borderWidth: 2,
                                };
                              },
                              afterBody: (context) => {
                                const dataIndex = context[0].dataIndex;
                                const trade = executiveStats.tradeMetrics[dataIndex];
                                return [`─────────────`, `Total: ${trade.totalDeficiencies}`];
                              },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                {/* Total Deficiencies by Trade */}
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
                }}>
                  <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px', color: '#48182F', textAlign: 'center' }}>
                    Total Deficiencies
                  </h2>
                  <div style={{ height: '350px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Doughnut
                      data={{
                        labels: executiveStats.tradeMetrics
                          .filter(t => t.trade !== 'Overall Average')
                          .map(t => t.trade),
                        datasets: [
                          {
                            data: executiveStats.tradeMetrics
                              .filter(t => t.trade !== 'Overall Average')
                              .map(t => t.totalDeficiencies),
                            backgroundColor: [
                              '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#3B82F6',
                              '#EF4444', '#84CC16', '#06B6D4', '#F97316', '#A78BFA',
                              '#60A5FA', '#34D399', '#FBBF24', '#F472B6', '#818CF8',
                              '#14B8A6', '#FB923C', '#FCD34D'
                            ],
                            borderWidth: 2,
                            borderColor: '#fff',
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: true,
                            position: 'right',
                            labels: {
                              font: {
                                size: 11,
                              },
                              padding: 10,
                              usePointStyle: true,
                            },
                          },
                          tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            padding: 12,
                            displayColors: true,
                            usePointStyle: true,
                            callbacks: {
                              label: (context) => {
                                const label = context.label || '';
                                const value = context.parsed || 0;
                                const total = executiveStats.tradeMetrics
                                  .filter(t => t.trade !== 'Overall Average')
                                  .reduce((sum, t) => sum + t.totalDeficiencies, 0);
                                const percentage = ((value / total) * 100).toFixed(1);
                                return `${label}: ${value} (${percentage}%)`;
                              },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                {/* Average Close-Out Time */}
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
                }}>
                  <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#48182F', textAlign: 'center' }}>
                    Average Close-Out Time (Days)
                  </h2>
                  <div style={{ height: '350px' }}>
                    <Line
                      data={{
                        labels: executiveStats.tradeMetrics
                          .filter(t => t.trade !== 'Overall Average')
                          .map(t => t.trade),
                        datasets: [
                          {
                            label: 'Avg. Close-Out Time',
                            data: executiveStats.tradeMetrics
                              .filter(t => t.trade !== 'Overall Average')
                              .map(t => parseFloat(t.avgCloseOutTime)),
                            borderColor: '#84CC16',
                            backgroundColor: 'rgba(132, 204, 22, 0.1)',
                            borderWidth: 2,
                            fill: true,
                            tension: 0.4,
                          },
                          {
                            label: 'Overall Average',
                            data: executiveStats.tradeMetrics
                              .filter(t => t.trade !== 'Overall Average')
                              .map(() => parseFloat(executiveStats.overallAvgCloseOutTime)),
                            borderColor: '#3B82F6',
                            backgroundColor: 'transparent',
                            borderWidth: 2,
                            borderDash: [5, 5],
                            pointRadius: 0,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          x: {
                            display: true,
                            grid: {
                              display: true,
                              color: '#F3F4F6',
                            },
                            ticks: {
                              font: {
                                size: 10,
                              },
                              maxRotation: 45,
                              minRotation: 45,
                            },
                          },
                          y: {
                            display: true,
                            beginAtZero: true,
                            grid: {
                              display: true,
                              color: '#F3F4F6',
                            },
                            ticks: {
                              font: {
                                size: 11,
                              },
                            },
                          },
                        },
                        plugins: {
                          legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                              usePointStyle: true,
                              padding: 15,
                              font: {
                                size: 12,
                              },
                            },
                          },
                          tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            padding: 12,
                            displayColors: true,
                            usePointStyle: true,
                            callbacks: {
                              label: (context) => {
                                const value = context.parsed.y ?? 0;
                                return `${context.dataset.label}: ${value.toFixed(2)} days`;
                              },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            )
          ) : loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : !selectedProjectId ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '48px',
              textAlign: 'center',
              color: '#6B7280',
              boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
            }}>
              <Building2 size={48} style={{ color: '#D4CBC4', marginBottom: '16px' }} />
              <p style={{ fontSize: '16px', margin: 0 }}>No projects available. Please create a project first.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Due Dates Overview */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
                  <div style={{
                    padding: '8px',
                    backgroundColor: 'rgba(72, 24, 47, 0.1)',
                    borderRadius: '8px',
                  }}>
                    <Calendar size={20} color="#48182F" />
                  </div>
                  <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>
                    Due Dates Overview
                  </h2>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px'
                }}>
                  <DueDateCard
                    title="Due This Week"
                    count={dueDates.thisWeek}
                    color="#3B82F6"
                    loading={loadingDueDates}
                  />
                  <DueDateCard
                    title="Due Next Week"
                    count={dueDates.nextWeek}
                    color="#F59E0B"
                    loading={loadingDueDates}
                  />
                  <DueDateCard
                    title="Over Due Deficiency"
                    count={dueDates.overdue}
                    color="#EF4444"
                    loading={loadingDueDates}
                  />
                </div>
              </div>

              {/* Average Turn Around Time */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
                  <div style={{
                    padding: '8px',
                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    borderRadius: '8px',
                  }}>
                    <Clock size={20} color="#F59E0B" />
                  </div>
                  <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>
                    Avg Turn Around Time / Inspection Type
                  </h2>
                </div>

                {loadingTurnAround ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <div className="builders-loading-spinner" style={{ margin: '0 auto' }} />
                  </div>
                ) : averageTurnAroundTime.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <p>No turn around time data available</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {averageTurnAroundTime.map((item, index) => {
                      const days = Math.floor(item.averageDays);
                      const hours = Math.floor((item.averageDays - days) * 24);
                      const mins = Math.floor(((item.averageDays - days) * 24 - hours) * 60);

                      return (
                        <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#48182F',
                            flexShrink: 0,
                            marginTop: '6px'
                          }} />
                          <div style={{ flex: 1 }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: '8px'
                            }}>
                              <div>
                                <div style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: '#48182F',
                                  marginBottom: '4px'
                                }}>
                                  {item.inspectionTypeName}
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#6B7280'
                                }}>
                                  {item.count} deficiencies
                                </div>
                              </div>
                              <div style={{
                                backgroundColor: '#F3F4F6',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#4B5563',
                                whiteSpace: 'nowrap'
                              }}>
                                {days} days {hours} hours {mins} mins
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Deficiencies by Status */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      padding: '8px',
                      backgroundColor: 'rgba(72, 24, 47, 0.1)',
                      borderRadius: '8px',
                    }}>
                      <BarChart3 size={20} color="#48182F" />
                    </div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>
                      Deficiencies by Status
                    </h2>
                  </div>

                  <select
                    value={statusChartInspectionTypeFilter || 'all'}
                    onChange={(e) => {
                      setStatusChartInspectionTypeFilter(e.target.value === 'all' ? null : e.target.value);
                      setTimeout(loadStatusCounts, 0);
                    }}
                    disabled={loadingStatus}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: statusChartInspectionTypeFilter ? '#48182F' : '#6B7280',
                      backgroundColor: loadingStatus ? 'rgba(245, 243, 240, 0.5)' : '#F5F3F0',
                      border: '1px solid rgba(212, 203, 196, 0.5)',
                      borderRadius: '8px',
                      cursor: loadingStatus ? 'not-allowed' : 'pointer',
                      outline: 'none',
                    }}
                  >
                    <option value="all">All Inspection Types</option>
                    {inspectionTypes.map((type) => (
                      <option key={type._id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                {loadingStatus ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <div className="builders-loading-spinner" style={{ margin: '0 auto' }} />
                  </div>
                ) : Object.keys(statusCounts).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <p>No status data available</p>
                  </div>
                ) : (
                  <div style={{ height: '300px' }}>
                    <Bar data={statusChartData} options={commonChartOptions} />
                  </div>
                )}
              </div>

              {/* Deficiencies by Inspection Type */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      padding: '8px',
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      borderRadius: '8px',
                    }}>
                      <BarChart3 size={20} color="#6366F1" />
                    </div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>
                      Deficiencies by Inspection Type
                    </h2>
                  </div>

                  <select
                    value={inspectionTypeChartStatusFilter || 'all'}
                    onChange={(e) => {
                      setInspectionTypeChartStatusFilter(e.target.value === 'all' ? null : e.target.value);
                      setTimeout(loadInspectionTypeCounts, 0);
                    }}
                    disabled={loadingInspectionType}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: inspectionTypeChartStatusFilter ? '#48182F' : '#6B7280',
                      backgroundColor: loadingInspectionType ? 'rgba(245, 243, 240, 0.5)' : '#F5F3F0',
                      border: '1px solid rgba(212, 203, 196, 0.5)',
                      borderRadius: '8px',
                      cursor: loadingInspectionType ? 'not-allowed' : 'pointer',
                      outline: 'none',
                    }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Closed">Closed</option>
                    <option value="Resolved">Resolved</option>
                  </select>
                </div>

                {loadingInspectionType ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <div className="builders-loading-spinner" style={{ margin: '0 auto' }} />
                  </div>
                ) : Object.keys(inspectionTypeCounts).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <p>No inspection type data available</p>
                  </div>
                ) : (
                  <div style={{ height: '300px' }}>
                    <Bar data={inspectionTypeChartData} options={commonChartOptions} />
                  </div>
                )}
              </div>

              {/* Trade Wise Data */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      padding: '8px',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      borderRadius: '8px',
                    }}>
                      <Users size={20} color="#10B981" />
                    </div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>
                      Trade Wise Data
                    </h2>
                  </div>

                  <select
                    value={tradeChartInspectionTypeFilter || 'all'}
                    onChange={(e) => {
                      setTradeChartInspectionTypeFilter(e.target.value === 'all' ? null : e.target.value);
                      setTimeout(loadTradeCounts, 0);
                    }}
                    disabled={loadingTrade}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: tradeChartInspectionTypeFilter ? '#48182F' : '#6B7280',
                      backgroundColor: loadingTrade ? 'rgba(245, 243, 240, 0.5)' : '#F5F3F0',
                      border: '1px solid rgba(212, 203, 196, 0.5)',
                      borderRadius: '8px',
                      cursor: loadingTrade ? 'not-allowed' : 'pointer',
                      outline: 'none',
                    }}
                  >
                    <option value="all">All Inspection Types</option>
                    {inspectionTypes.map((type) => (
                      <option key={type._id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                {loadingTrade ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <div className="builders-loading-spinner" style={{ margin: '0 auto' }} />
                  </div>
                ) : Object.keys(tradeCounts).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <p>No trade data available</p>
                  </div>
                ) : (
                  <div style={{ height: '300px' }}>
                    <Bar data={tradeChartData} options={commonChartOptions} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

// Due Date Card Component
interface DueDateCardProps {
  title: string;
  count: number;
  color: string;
  loading: boolean;
}

const DueDateCard: React.FC<DueDateCardProps> = ({ title, count, color, loading }) => {
  return (
    <div style={{
      backgroundColor: '#F5F3F0',
      border: `1.5px solid ${color}33`,
      borderRadius: '12px',
      padding: '20px',
      boxShadow: `0 2px 8px ${color}15`,
    }}>
      <p style={{
        fontSize: '13px',
        fontWeight: '600',
        color: '#6B7280',
        margin: '0 0 12px 0',
        lineHeight: 1.3
      }}>
        {title}
      </p>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '10px' }}>
          <div className="builders-loading-spinner" style={{ width: '24px', height: '24px', margin: '0 auto' }} />
        </div>
      ) : (
        <p style={{
          fontSize: '32px',
          fontWeight: '700',
          color: color,
          margin: 0
        }}>
          {count}
        </p>
      )}
    </div>
  );
};

export default ServiceDashboard;
